import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import { useAuth } from '../context/AuthContext';
import './Guide.css';

interface Msg { from: 'user' | 'ai'; text: string }

interface Feature {
  id: string;
  badge: string;
  title: string;
  description: string;
  tips?: string[];
  examples: Msg[][];
}

function SmsThread({ messages }: { messages: Msg[] }) {
  return (
    <div className="guide-sms-thread">
      {messages.map((m, i) => (
        <div
          key={i}
          className={`guide-sms-bubble ${m.from === 'user' ? 'guide-sms-user' : 'guide-sms-ai'}`}
        >
          {m.text}
        </div>
      ))}
    </div>
  );
}

const features: Feature[] = [
  {
    id: 'log-food',
    badge: 'Diet',
    title: 'Log meals & drinks',
    description:
      'Describe what you ate in plain language — or send a photo of your meal. Mosaime extracts nutrition details automatically.',
    examples: [
      [
        { from: 'user', text: 'Had a turkey wrap with spinach for lunch' },
        { from: 'ai', text: 'Logged! Lean protein and fiber — solid midday meal.' },
      ],
      [
        { from: 'user', text: '[sends photo of plate]' },
        { from: 'ai', text: 'Looks like grilled salmon with roasted veggies — logged! Great choice for protein and omega-3s.' },
      ],
    ],
  },
  {
    id: 'log-workout',
    badge: 'Exercise',
    title: 'Log workouts & movement',
    description:
      'Log any activity — a gym session, a walk, a run, yoga, or anything else. Just describe it naturally.',
    examples: [
      [
        { from: 'user', text: 'Just finished a 5K run — 28 minutes' },
        { from: 'ai', text: "Great pace! 5K run logged. You're on a 3-day streak this week." },
      ],
      [
        { from: 'user', text: 'Did 45 minutes of yoga this morning' },
        { from: 'ai', text: 'Logged! Great for both body and mind.' },
      ],
    ],
  },
  {
    id: 'log-mood',
    badge: 'Mind',
    title: 'Track mood, sleep & stress',
    description:
      "Share how you're feeling, how you slept, or how stressed you are. Mosaime logs this as your mental wellness data and tracks it over time.",
    examples: [
      [
        { from: 'user', text: "Didn't sleep well — maybe 5 hours and kept waking up" },
        { from: 'ai', text: 'Noted. Poor sleep can affect everything. Want a few tips to help tonight?' },
      ],
      [
        { from: 'user', text: 'Feeling pretty overwhelmed with work this week' },
        { from: 'ai', text: "Logged. That's real — let's keep an eye on your stress pattern. Try one small thing to decompress today." },
      ],
    ],
  },
  {
    id: 'food-scan',
    badge: 'Food Scan',
    title: 'Scan food ingredients',
    description:
      "Send a photo of any packaged product's ingredient list and Mosaime generates a full breakdown — flagging additives and health concerns based on EWG ratings. Works on cosmetics and cleaning products too.",
    tips: [
      'Say "can you scan these ingredients?" when sending a label photo',
      'Or describe the product name and ask Mosaime to look it up',
      'Works best with ultra-processed or packaged foods',
    ],
    examples: [
      [
        { from: 'user', text: 'Can you scan the ingredients on this? [sends label photo]' },
        {
          from: 'ai',
          text: "Got it! Here's your full ingredient breakdown: mosaime.com/foodscan/abc123 — a few additives worth noting.",
        },
      ],
      [
        { from: 'user', text: 'I just had a Clif Bar — can you check the ingredients?' },
        {
          from: 'ai',
          text: 'Sure! Logging your Clif Bar now. If you have the package handy, send a photo of the ingredient list and I\'ll generate a full breakdown for you.',
        },
      ],
    ],
  },
  {
    id: 'recipes',
    badge: 'Recipes',
    title: 'Get recipe ideas',
    description:
      "Ask for recipes based on what you're in the mood for, your dietary needs, or ingredients you have. Mosaime searches its library first, or generates a custom recipe for you.",
    tips: [
      'Be specific — mention a protein, cuisine, or dietary preference',
      'Say "save it" to bookmark a recipe for later',
      'Say "I made it" to mark a recipe as completed — it improves future suggestions',
      'Ask for a shopping list based on recipes you want to make',
    ],
    examples: [
      [
        { from: 'user', text: "I'm looking for a high-protein dinner with chicken" },
        {
          from: 'ai',
          text: 'Found 3 options — lemon herb chicken bowl, grilled chicken with roasted broccoli, or a Thai chicken stir-fry. Which sounds good?',
        },
      ],
      [
        { from: 'user', text: 'Create me a custom gluten-free meal prep breakfast' },
        {
          from: 'ai',
          text: "Here's a custom recipe: Overnight oat cups with almond butter and banana — mosaime.com/recipes/xyz789",
        },
      ],
    ],
  },
  {
    id: 'workout-plan',
    badge: 'Workout Plan',
    title: 'Build a workout plan',
    description:
      "Ask Mosaime to design a custom multi-week training program. You'll get daily workout links via SMS, and can edit or adjust the plan at any time.",
    tips: [
      'Mosaime will ask a few quick questions before generating your plan',
      'Use the edit link in your plan to change exercises, days, or delivery times',
      'Say "make this week easier" or "increase the intensity" to scale up or down',
      'Say "delete my plan" to cancel it and start fresh',
    ],
    examples: [
      [
        { from: 'user', text: 'Can you build me a beginner strength plan, 3 days a week?' },
        {
          from: 'ai',
          text: "Let's do it! What's your goal — build muscle, lose weight, or general fitness? And what time should I send daily workouts?",
        },
      ],
      [
        { from: 'user', text: 'Build muscle. Send workouts at 7am.' },
        {
          from: 'ai',
          text: 'Your 8-week beginner strength plan is ready! First workout tomorrow at 7am. View and edit your plan: mosaime.com/plan/abc123',
        },
      ],
    ],
  },
  {
    id: 'single-workout',
    badge: 'Exercise',
    title: 'Get a one-off workout',
    description:
      "Want a quick session today without committing to a full plan? Just ask for it. Mosaime generates a workout based on your equipment, time, and preferences.",
    examples: [
      [
        { from: 'user', text: "Give me a 20-minute HIIT workout I can do at home" },
        {
          from: 'ai',
          text: 'Here\'s your 20-min HIIT session — no equipment needed: mosaime.com/workout/abc123',
        },
      ],
      [
        { from: 'user', text: "I want to do some yoga today — 30 minutes, beginner level" },
        {
          from: 'ai',
          text: "Here's a 30-min beginner yoga flow for you: mosaime.com/workout/def456",
        },
      ],
    ],
  },
  {
    id: 'goals',
    badge: 'Goals',
    title: 'Set & track goals',
    description:
      "Tell Mosaime what you're working toward. Goals are tracked over time and Mosaime will proactively coach you on progress and suggest habits to support them.",
    examples: [
      [
        { from: 'user', text: 'I want to lose 15 pounds by the summer' },
        {
          from: 'ai',
          text: "Goal logged! When you say summer, are you thinking around June? And would you rather focus on diet, exercise, or both? Let's make a plan.",
        },
      ],
      [
        { from: 'user', text: 'What are my current goals?' },
        {
          from: 'ai',
          text: 'You have 2 active goals: (1) Lose 15 pounds — no target date yet. (2) Run a 5K — targeted for June 30.',
        },
      ],
    ],
  },
  {
    id: 'habits',
    badge: 'Habits',
    title: 'Build habits & set reminders',
    description:
      "Set up recurring habits — daily stretches, meal prep Sundays, hydration check-ins, or anything else. Mosaime sends SMS reminders and tracks your streaks.",
    tips: [
      "Mosaime will ask for a time if you don't specify one",
      'Say "show me my habits" to see all active routines',
      'Say "cancel my [habit name]" to remove one',
    ],
    examples: [
      [
        { from: 'user', text: 'Remind me to stretch every morning at 7am' },
        { from: 'ai', text: "Done! Morning stretch reminder set for 7am daily. I'll check in after your first one." },
      ],
      [
        { from: 'user', text: 'I want to start meal prepping every Sunday' },
        { from: 'ai', text: 'Sunday meal prep habit added! What time should I send your prep reminder?' },
      ],
    ],
  },
  {
    id: 'progress',
    badge: 'Progress',
    title: 'Check your progress',
    description:
      "Ask how you're doing on any wellness area — calories, macros, workouts, sleep, stress, or anything else. Mosaime analyzes your logged data and summarizes it in plain language.",
    examples: [
      [
        { from: 'user', text: 'How have I been eating this week?' },
        {
          from: 'ai',
          text: 'This week: 5 meals logged, avg 1,850 cal/day. Strong on protein, a bit light on fiber. Best day was Tuesday.',
        },
      ],
      [
        { from: 'user', text: "How's my stress been trending this month?" },
        {
          from: 'ai',
          text: "Your stress scores have been moderate — higher mid-week, better on weekends. Sleep quality seems to correlate. Want to set up a wind-down routine?",
        },
      ],
    ],
  },
  {
    id: 'chart',
    badge: 'Wellness Chart',
    title: 'Request your wellness chart',
    description:
      'Ask for your mosaic chart at any time to get a visual overview of all your diet, exercise, and mental wellness scores over the past year.',
    examples: [
      [
        { from: 'user', text: 'Show me my wellness chart' },
        {
          from: 'ai',
          text: "Here's your mosaic: mosaime.com/charts/abc123 — looking green! Your exercise scores have been your strongest this month.",
        },
      ],
      [
        { from: 'user', text: 'Can I see my mosaic?' },
        {
          from: 'ai',
          text: "Of course! Here you go: mosaime.com/charts/abc123 — diet has been consistent and exercise is picking up this week.",
        },
      ],
    ],
  },
  {
    id: 'fitbit',
    badge: 'Fitbit',
    title: 'Ask about your Fitbit data',
    description:
      "If you've connected a Fitbit in your account settings, Mosaime can pull your sleep and step data and factor it into your coaching.",
    tips: ['Connect Fitbit from your account settings at mosaime.com/settings'],
    examples: [
      [
        { from: 'user', text: 'How did I sleep last night?' },
        {
          from: 'ai',
          text: "Last night: 6h 12m total, 1h 8m deep sleep — a bit below your average. Try winding down 30 minutes earlier tonight.",
        },
      ],
      [
        { from: 'user', text: 'What were my steps this week?' },
        {
          from: 'ai',
          text: 'This week: avg 7,200 steps/day. You hit 10K on Wednesday and Thursday — great days!',
        },
      ],
    ],
  },
  {
    id: 'preferences',
    badge: 'Profile',
    title: 'Update your preferences',
    description:
      'Tell Mosaime anything about yourself — dietary restrictions, injuries, equipment you have, foods you dislike, or schedule changes. It remembers everything and adjusts coaching accordingly.',
    examples: [
      [
        { from: 'user', text: "I'm lactose intolerant — can you remember that?" },
        {
          from: 'ai',
          text: "Noted! I'll avoid dairy in future recipe suggestions and flag any high-lactose foods you log.",
        },
      ],
      [
        { from: 'user', text: "I hurt my knee, I can't run for a couple weeks" },
        {
          from: 'ai',
          text: "Got it — knee injury noted. I'll swap running out of your plan and avoid high-impact suggestions while you recover.",
        },
      ],
    ],
  },
  {
    id: 'general',
    badge: 'General',
    title: 'Ask any wellness question',
    description:
      "Have a general wellness question? Just ask. Mosaime can help with nutrition, sleep, stress management, workout science, meal planning, and more.",
    examples: [
      [
        { from: 'user', text: 'What are some good high-fiber foods I should be eating more of?' },
        {
          from: 'ai',
          text: 'Top picks: lentils, chia seeds, oats, black beans, avocado, broccoli, and berries. Aim for 25–35g per day.',
        },
      ],
      [
        { from: 'user', text: 'How do I know if I\'m overtraining?' },
        {
          from: 'ai',
          text: "Key signs: persistent soreness, declining performance, poor sleep, irritability, and loss of motivation. Rest days are part of the plan!",
        },
      ],
    ],
  },
];

const importantNotes = [
  {
    icon: '⏱',
    title: 'Responses have a short delay',
    text: 'Mosaime processes messages using AI, so expect a 5–15 second delay before a reply arrives. This is normal — no need to resend if you don\'t hear back immediately.',
  },
  {
    icon: '🚫',
    title: 'Not for medical advice',
    text: 'Mosaime is a wellness assistant, not a medical provider. It cannot diagnose conditions, recommend medications, or provide mental health therapy. Always consult a healthcare professional for medical concerns.',
  },
  {
    icon: '✉',
    title: 'Account & billing help',
    text: 'For subscription changes, billing questions, or to connect your Fitbit, visit mosaime.com/settings. To report a bug or give feedback, visit mosaime.com/feedback.',
  },
  {
    icon: '🛑',
    title: 'Stopping messages',
    text: 'Text STOP at any time to stop receiving messages. You can rejoin through your account settings at mosaime.com/settings.',
  },
];

const badgeColorMap: Record<string, string> = {
  Diet: '#4caf50',
  Exercise: '#2196f3',
  Mind: '#9c27b0',
  'Food Scan': '#ff7043',
  Recipes: '#ff9800',
  'Workout Plan': '#1976d2',
  Goals: '#e91e63',
  Habits: '#00897b',
  Progress: '#558b2f',
  'Wellness Chart': '#2e7d32',
  Fitbit: '#00bcd4',
  Profile: '#795548',
  General: '#607d8b',
};

export default function Guide() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="guide-page landing-page">
      <PublicHeader />

      <section className="guide-hero">
        <div className="landing-container">
          <h1>Mosaime User Guide</h1>
          <p className="guide-hero-sub">
            Mosaime works entirely over SMS — just text it like you would a friend. Here's a
            reference for everything you can do.
          </p>
          <div className="guide-hero-note">
            Not getting a reply? Check that you're texting the right number and see the tips at the
            bottom of this page.
          </div>
        </div>
      </section>

      <section className="guide-toc-section">
        <div className="landing-container">
          <div className="guide-toc">
            {features.map((f) => (
              <a key={f.id} href={`#${f.id}`} className="guide-toc-chip">
                {f.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="guide-features-section">
        <div className="landing-container">
          {features.map((feature) => (
            <div key={feature.id} id={feature.id} className="guide-feature-card">
              <div className="guide-feature-header">
                <span
                  className="guide-feature-badge"
                  style={{ background: badgeColorMap[feature.badge] ?? 'var(--primary)' }}
                >
                  {feature.badge}
                </span>
                <h2 className="guide-feature-title">{feature.title}</h2>
              </div>
              <p className="guide-feature-desc">{feature.description}</p>

              {feature.tips && feature.tips.length > 0 && (
                <ul className="guide-tips-list">
                  {feature.tips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              )}

              <div className="guide-examples">
                {feature.examples.map((thread, i) => (
                  <SmsThread key={i} messages={thread} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="guide-notes-section">
        <div className="landing-container">
          <h2 className="guide-notes-heading">Important notes</h2>
          <div className="guide-notes-grid">
            {importantNotes.map((note, i) => (
              <div key={i} className="guide-note-card">
                <div className="guide-note-icon">{note.icon}</div>
                <div>
                  <div className="guide-note-title">{note.title}</div>
                  <p className="guide-note-text">{note.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {!isAuthenticated && (
        <section className="section cta-section">
          <div className="landing-container cta-inner">
            <h2>Not a member yet?</h2>
            <Link to="/signup" className="btn hero-cta">
              Get Started Free
            </Link>
          </div>
        </section>
      )}

      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/features">Features</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/guide">Guide</Link>
            <Link to="/philosophy">Philosophy</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
